/**
 * ProductDetail Page - Mix Design / Recipe summary, read top to bottom:
 *
 *   header  what the mix is (code, description, grade, revision, status),
 *           Edit mix design, "..." (activate / deactivate, delete), audit line
 *   row 1   Monitoring settings (maturity equation, temperature and
 *           evaporation limits, default strength targets, batching plants)
 *           | Strength vs maturity calibration (active curve + top 3)
 *   row 2   Strength targets | Temperature profile
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productsService, {
  Product,
  type SupplierPlant,
  type ProductPlantItem,
  type MassConcreteDefault,
  type EvaporationDefault,
  type TargetGroup,
} from '../services/products';
import ProductTempProfileCard from '../components/ProductTempProfileCard';
import ProductTargetGroupsTab from '../components/ProductTargetGroupsTab';
import ProductCalibrationsTab from '../components/ProductCalibrationsTab';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { useCompanyStrengthUnit } from '../utils/useCompanyStrengthUnit';
import { maturityConstantsDetail, maturityEquationName } from '../utils/maturityEquation';
import { apiErrorText } from '../utils/apiError';

/** Audit timestamps are stored as naive UTC — parse them as UTC and render
 *  in the browser's local time (see timezone convention in datetime.ts). */
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** One page section: a card with a sentence-case heading. */
const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: 'anywhere' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const ProductDetail: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'mix_designs.edit');
  const canDelete = hasPermission(user, 'mix_designs.deactivate');
  const canLinkPlants = hasPermission(user, 'mix_designs.link_batch_plants') || canManage;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [plantsOpen, setPlantsOpen] = useState(false);

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Lookups for resolving the default group names in the Details panel
  const [massConcreteDefaults, setMassConcreteDefaults] = useState<MassConcreteDefault[]>([]);
  const [evaporationDefaults, setEvaporationDefaults] = useState<EvaporationDefault[]>([]);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);

  // Grade strength is in the mix design company's unit (StrengthFormat);
  // shown without a unit until that is known.
  const strengthUnit = useCompanyStrengthUnit(product?.company_id);

  // Plant linking
  const [allPlants, setAllPlants] = useState<SupplierPlant[]>([]);
  const [plantLinks, setPlantLinks] = useState<ProductPlantItem[]>([]);
  const [togglingPlantId, setTogglingPlantId] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const [data, plants, links, mcDefaults, evapDefaults, groups] = await Promise.all([
        productsService.getProduct(productId),
        productsService.listSupplierPlants(undefined, true).catch(() => [] as SupplierPlant[]),
        productsService.getProductPlants(productId).catch(() => [] as ProductPlantItem[]),
        productsService.listMassConcreteDefaults().catch(() => [] as MassConcreteDefault[]),
        productsService.listEvaporationDefaults().catch(() => [] as EvaporationDefault[]),
        productsService.listTargetGroups(productId).catch(() => [] as TargetGroup[]),
      ]);
      setProduct(data);
      setAllPlants(plants);
      setPlantLinks(links);
      setMassConcreteDefaults(mcDefaults);
      setEvaporationDefaults(evapDefaults);
      setTargetGroups(groups);
    } catch (err: any) {
      setError(apiErrorText(err, t('products.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId, loadProduct]);

  const linkedIds = useMemo(
    () => new Set(plantLinks.map((l) => l.supplier_plant_id)),
    [plantLinks]
  );

  const handleTogglePlant = async (plant: SupplierPlant) => {
    if (!productId) return;
    const isLinked = linkedIds.has(plant.supplier_plant_id);
    setTogglingPlantId(plant.supplier_plant_id);
    try {
      if (isLinked) {
        await productsService.unlinkSupplierPlantProduct(plant.supplier_plant_id, productId);
        setPlantLinks((prev) => prev.filter((l) => l.supplier_plant_id !== plant.supplier_plant_id));
        setSuccess(t('productDetail.plantUnlinked', 'Batching plant unlinked'));
      } else {
        const item = await productsService.linkSupplierPlantProduct(plant.supplier_plant_id, productId);
        setPlantLinks((prev) => [
          ...prev,
          {
            supplier_plant_id: plant.supplier_plant_id,
            name: plant.name,
            ready_mix_supplier: plant.ready_mix_supplier ?? null,
            date_linked: item.date_linked ?? null,
          },
        ]);
        setSuccess(t('productDetail.plantLinked', 'Batching plant linked'));
      }
    } catch (err: any) {
      setError(apiErrorText(err, t('productDetail.plantToggleFailed', 'Failed to update plant link')));
    } finally {
      setTogglingPlantId(null);
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleEdit = () => {
    navigate(`/products/${productId}/edit`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('products.productNotFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          {t('products.backToProducts')}
        </Button>
      </Box>
    );
  }

  const active = product.is_active !== false;
  const tempGroupName = massConcreteDefaults.find(
    (d) => d.id === product.mass_concrete_default_id
  )?.description;
  const defaultTargetsName = targetGroups.find(
    (g) => g.target_group_id === product.target_group_id
  )?.target_group_name;
  const evapGroupName = evaporationDefaults.find(
    (d) => d.id === product.evaporation_default_id
  )?.description;

  const notSet = (text = t('productDetail.notSet', 'Not set')) => (
    <Typography component="span" variant="body2" color="text.disabled">{text}</Typography>
  );
  const facts = [
    product.description,
    product.strength_grade != null ? `${product.strength_grade}${strengthUnit ? ` ${strengthUnit}` : ''}` : null,
    product.rev ? `Rev ${product.rev}` : null,
  ].filter(Boolean).join(' · ');
  const audit = [
    product.date_create
      ? `${t('productDetail.created', 'Created')} ${formatDateTime(product.date_create)}${product.create_by ? ` by ${product.create_by}` : ''}`
      : null,
    product.date_last_modified
      ? `${t('productDetail.lastChanged', 'last changed')} ${formatDateTime(product.date_last_modified)}${product.modified_by ? ` by ${product.modified_by}` : ''}`
      : t('productDetail.notChanged', 'not changed since'),
  ].filter(Boolean).join(' · ');

  const handleToggleActive = async () => {
    setMenuAnchor(null);
    try {
      await productsService.updateProduct(product.product_id, { is_active: !active });
      setSuccess(active ? t('products.deactivated', 'Mix design deactivated') : t('products.activated', 'Mix design activated'));
      loadProduct();
    } catch (err: any) {
      setError(apiErrorText(err, t('products.toggleFailed', 'Failed to update status')));
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    if (!window.confirm(t('productDetail.deleteConfirm', `Delete mix design ${product.code}?`))) return;
    try {
      await productsService.deleteProduct(product.product_id);
      navigate('/products');
    } catch (err: any) {
      setError(apiErrorText(err, t('products.failedToDelete', 'Failed to delete')));
    }
  };

  return (
    <Box>
      {/* Header: what the mix is, at a glance */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ textTransform: 'none' }}>
          {t('common.back')}
        </Button>
        <Typography variant="h6" component="h1">{product.code}</Typography>
        {facts && <Typography variant="body1" color="text.secondary">{facts}</Typography>}
        <Chip
          size="small"
          label={active ? t('products.statusActive', 'Active') : t('products.statusInactive', 'De-Active')}
          color={active ? 'success' : 'default'}
        />
        <Box sx={{ flexGrow: 1 }} />
        {canManage && (
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleEdit} sx={{ textTransform: 'none' }}>
            {t('productDetail.editMix', 'Edit mix design')}
          </Button>
        )}
        {(canManage || canDelete) && (
          <>
            <IconButton size="small" aria-label="more actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              {canManage && (
                <MenuItem onClick={handleToggleActive}>
                  {active ? t('productDetail.deactivate', 'Deactivate') : t('productDetail.activate', 'Activate')}
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>{t('common.delete', 'Delete')}</MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ ml: 1, mb: 2 }}>
        {audit}
      </Typography>

      {!active && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('productDetail.inactiveBanner', 'This mix design is deactivated and hidden from active lists and the pour wizard.')}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Row 1 left: what the mix uses when it is monitored */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard title={t('productDetail.settingsTitle', 'Monitoring settings')}>
            <DetailRow
              label={t('productDetail.maturityEquation', 'Maturity equation')}
              value={
                maturityEquationName(product) ? (
                  <>
                    {maturityEquationName(product)}
                    {maturityConstantsDetail(product) && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {` (${maturityConstantsDetail(product)})`}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography component="span" variant="body2" color="warning.main" data-testid="no-maturity-equation">
                    {t('productDetail.noMaturityEquation', 'Not set: pours use the system default')}
                  </Typography>
                )
              }
            />
            <DetailRow label={t('productDetail.tempGroup', 'Temperature limits')} value={tempGroupName || notSet()} />
            <DetailRow label={t('productDetail.evapGroup', 'Evaporation limits')} value={evapGroupName || notSet()} />
            <DetailRow label={t('productDetail.strengthGroup', 'Default strength targets')} value={defaultTargetsName || notSet()} />
            <DetailRow
              label={t('productDetail.linkedPlants', 'Batching plants')}
              value={
                <>
                  {plantLinks.length > 0
                    ? plantLinks.map((l) => l.name).join(', ')
                    : notSet(t('productDetail.noLinkedPlants', 'None linked'))}
                  {canLinkPlants && allPlants.length > 0 && (
                    <Link component="button" variant="body2" sx={{ ml: 1, verticalAlign: 'baseline' }}
                      onClick={() => setPlantsOpen(true)}>
                      {t('productDetail.changePlants', 'Change')}
                    </Link>
                  )}
                </>
              }
            />
          </SectionCard>
        </Grid>

        {/* Row 1 right: the strength curve pours of this mix use */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title={t('productDetail.calibrationsTitle', 'Strength vs maturity calibration')}>
            <ProductCalibrationsTab
              compact
              productId={productId!}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>
        </Grid>

        {/* Row 2 left: strength targets used by pour setup */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title={t('productDetail.tabTargetGroups', 'Strength targets')}>
            <ProductTargetGroupsTab
              productId={productId!}
              defaultGroupId={product.target_group_id ?? null}
              maturityMethod={product.maturity_method ?? null}
              specifiedTemp={product.maturity_specified_temp ?? null}
              strengthUnit={strengthUnit}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>
        </Grid>

        {/* Row 2 right: example temperature curves */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard title={t('productDetail.temperatureProfile', 'Temperature profile')}>
            <ProductTempProfileCard
              productId={productId!}
              canManage={canManage}
              onError={(msg) => setError(msg)}
              onSuccess={(msg) => setSuccess(msg)}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Batching plants: pick which plants supply this mix */}
      <Dialog open={plantsOpen} onClose={() => setPlantsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('productDetail.plantsDialogTitle', 'Batching plants for this mix')}</DialogTitle>
        <DialogContent dividers>
          {allPlants.map((plant) => (
            <FormControlLabel
              key={plant.supplier_plant_id}
              sx={{ display: 'flex' }}
              control={
                <Checkbox
                  size="small"
                  checked={linkedIds.has(plant.supplier_plant_id)}
                  disabled={togglingPlantId === plant.supplier_plant_id}
                  onChange={() => handleTogglePlant(plant)}
                />
              }
              label={
                <span>
                  {plant.name}
                  {plant.ready_mix_supplier && (
                    <Typography component="span" variant="body2" color="text.secondary">{` · ${plant.ready_mix_supplier}`}</Typography>
                  )}
                </span>
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantsOpen(false)}>{t('common.done', 'Done')}</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDetail;
